import { useState, useCallback, useEffect } from 'react';
import { calculateZoom } from '../utils/imageTransform';

interface UseZoomControlsProps {
  isZoomLocked: boolean;
  onTransformChange?: (scale: number, rotation: number) => void;
  onPositionChange?: (position: { x: number; y: number }) => void;
  currentPosition?: { x: number; y: number };
  containerRef?: React.RefObject<HTMLDivElement | null>;
}

const BASE_ZOOM_STEP = 0.005;
const MIN_SCALE = 0.2;
const MAX_SCALE = 2.5;

interface ZoomStepOptions {
  stepScale?: number;
}

export const useZoomControls = ({ 
  isZoomLocked, 
  onTransformChange, 
  onPositionChange,
  currentPosition = { x: 0, y: 0 },
  containerRef 
}: UseZoomControlsProps) => {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [zoomInterval, setZoomInterval] = useState<NodeJS.Timeout | null>(null);

  // Function to adjust position to keep top-left corner fixed during zoom
  const adjustPositionForZoom = useCallback((newScale: number, oldScale: number) => {
    // With transform-origin: top left, we don't need to adjust position
    // The browser will automatically anchor to the top-left corner
    // This function is kept for potential future use but doesn't modify position
  }, []);

  const applyZoomDelta = useCallback((delta: number) => {
    setScale(prev => {
      const next = calculateZoom(prev, delta, MIN_SCALE, MAX_SCALE);
      adjustPositionForZoom(next, prev);
      onTransformChange?.(next, rotation);
      return next;
    });
  }, [adjustPositionForZoom, onTransformChange, rotation]);

  const zoomIn = useCallback((e: React.MouseEvent | React.WheelEvent, options?: ZoomStepOptions) => {
    if (isZoomLocked) return;
    e.stopPropagation();
    const stepScale = options?.stepScale ?? 1;
    applyZoomDelta(BASE_ZOOM_STEP * stepScale);
  }, [isZoomLocked, applyZoomDelta]);

  const zoomOut = useCallback((e: React.MouseEvent | React.WheelEvent, options?: ZoomStepOptions) => {
    if (isZoomLocked) return;
    e.stopPropagation();
    const stepScale = options?.stepScale ?? 1;
    applyZoomDelta(-BASE_ZOOM_STEP * stepScale);
  }, [isZoomLocked, applyZoomDelta]);

  const zoomInStart = useCallback((e: React.MouseEvent) => {
    if (isZoomLocked) return;
    e.stopPropagation();
    zoomIn(e);
    
    const interval = setInterval(() => {
      applyZoomDelta(BASE_ZOOM_STEP);
    }, 100);
    
    setZoomInterval(interval);
  }, [isZoomLocked, zoomIn, applyZoomDelta]);

  const zoomOutStart = useCallback((e: React.MouseEvent) => {
    if (isZoomLocked) return;
    e.stopPropagation();
    zoomOut(e);
    
    const interval = setInterval(() => {
      applyZoomDelta(-BASE_ZOOM_STEP);
    }, 100);
    
    setZoomInterval(interval);
  }, [isZoomLocked, zoomOut, applyZoomDelta]);

  const zoomStop = useCallback(() => {
    if (zoomInterval) {
      clearInterval(zoomInterval);
      setZoomInterval(null);
    }
  }, [zoomInterval]);

  const resetZoom = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setScale(1);
    setRotation(0);
    onPositionChange?.({ x: 0, y: 0 });
    onTransformChange?.(1, 0);
  }, [onTransformChange, onPositionChange]);

  const rotateLeft = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const newRotation = rotation - 90;
    setRotation(newRotation);
    onTransformChange?.(scale, newRotation);
  }, [rotation, scale, onTransformChange]);

  const rotateRight = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const newRotation = rotation + 90;
    setRotation(newRotation);
    onTransformChange?.(scale, newRotation);
  }, [rotation, scale, onTransformChange]);

  const toggleZoom = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (scale > 1) {
      resetZoom(e);
    } else {
      const newScale = 2;
      setScale(newScale);
      adjustPositionForZoom(newScale, scale);
      onTransformChange?.(newScale, rotation);
    }
  }, [scale, rotation, resetZoom, onTransformChange, adjustPositionForZoom]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (zoomInterval) {
        clearInterval(zoomInterval);
      }
    };
  }, [zoomInterval]);

  return {
    scale,
    rotation,
    zoomIn,
    zoomOut,
    zoomInStart,
    zoomOutStart,
    zoomStop,
    resetZoom,
    rotateLeft,
    rotateRight,
    toggleZoom,
    setScale,
    setRotation,
  };
}; 